import React, { useState } from 'react';
import { useGamification } from './hooks/useGamification';
import GamificationServices from './services/GamificationServices';
import LevelProgressCard from './components/LevelProgressCard';
import XPStatsCard from './components/XPStatsCard';
import AchievementsCard from './components/AchievementsCard';
import LeaderboardCard from './components/LeaderboardCard';
import WeeklyStatsCard from './components/WeeklyStatsCard';
import RecentActivityCard from './components/RecentActivityCard';
import TierBadge from './components/TierBadge';
import './styles/gamification.css';

const GamificationDashboard = () => {
  const { data, loading, error, isEligible, refresh } = useGamification();
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refresh();
    setRefreshing(false);
  };

  if (loading) {
    return (
      <div className="gamification-container">
        <LoadingState />
      </div>
    );
  }

  if (error) {
    return (
      <div className="gamification-container">
        <ErrorState error={error} onRetry={handleRefresh} />
      </div>
    );
  }

  if (isEligible === false) {
    return (
      <div className="gamification-container">
        <NotEligibleState />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="gamification-container">
        <LoadingState />
      </div>
    );
  }

  const { profile, achievements, weekly_stats, ranking, recent_activity } = data;

  return (
    <div className="gamification-container">
      {/* Modern Header Section */}
      <div className="gamification-hero animate-in">
        <div className="hero-background"></div>
        <div className="hero-content">
          <div className="hero-icon-container animate-bounce">
            <i className="las la-trophy hero-icon"></i>
          </div>
          <h1 className="hero-title">Gamificação</h1>
          <p className="hero-subtitle">
            Conquiste XP, desbloqueie conquistas e evolua seus níveis
          </p>
          <div className="hero-badge-container">
            <TierBadge tier={profile.tier_info} />
          </div>
        </div>
      </div>

      {/* Floating Refresh Button */}
      <div className="refresh-container">
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="refresh-btn"
          aria-label="Atualizar dados"
        >
          <i className={`las la-sync-alt ${refreshing ? 'refresh-spinning' : ''}`}></i>
          <span className="refresh-text">
            {refreshing ? 'Atualizando...' : 'Atualizar'}
          </span>
        </button>
      </div>

      {/* Stats Overview Cards */}
      <div className="stats-overview">
        <div className="animate-in" style={{ animationDelay: '0.1s' }}>
          <LevelProgressCard profile={profile} />
        </div>
        <div className="animate-in" style={{ animationDelay: '0.2s' }}>
          <XPStatsCard profile={profile} />
        </div>
      </div>

      {/* Secondary Stats Grid */}
      <div className="secondary-stats">
        <div className="secondary-card animate-in" style={{ animationDelay: '0.3s' }}>
          <WeeklyStatsCard weeklyStats={weekly_stats} />
        </div>
        <div className="secondary-card animate-in" style={{ animationDelay: '0.4s' }}>
          <RankingCard ranking={ranking} />
        </div>
      </div>

      {/* Achievements & Activity Section */}
      <div className="engagement-section">
        <div className="animate-in" style={{ animationDelay: '0.5s' }}>
          <AchievementsCard achievements={achievements} />
        </div>
        <div className="animate-in" style={{ animationDelay: '0.6s' }}>
          <RecentActivityCard activities={recent_activity} />
        </div>
      </div>

      {/* Leaderboard Feature */}
      <div className="leaderboard-section animate-in" style={{ animationDelay: '0.7s' }}>
        <LeaderboardCard />
      </div>
    </div>
  );
};

const LoadingState = () => (
  <div className="loading-container">
    <div className="loading-spinner">
      <i className="las la-gamepad"></i>
    </div>
    <div className="loading-text">Carregando seu progresso...</div>
  </div>
);

const ErrorState = ({ error, onRetry }) => {
  const handleShowError = () => {
    GamificationServices.showErrorModal(error);
  };

  return (
    <div className="error-container">
      <div className="error-icon">
        <i className="las la-exclamation-triangle"></i>
      </div>
      <h2 className="error-title">Oops! Algo deu errado</h2>
      <p className="error-message">
        Não foi possível carregar seus dados de gamificação.
      </p>
      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
        <button onClick={onRetry} className="retry-button">
          <i className="las la-redo-alt me-2"></i>
          Tentar Novamente
        </button>
        <button 
          onClick={handleShowError} 
          className="btn btn-outline-danger"
          style={{ padding: '0.75rem 1.5rem' }}
        >
          Ver Detalhes
        </button>
      </div>
    </div>
  );
};

const NotEligibleState = () => (
  <div className="not-eligible-container">
    <div className="not-eligible-icon">
      <i className="las la-lock"></i>
    </div>
    <h2 className="not-eligible-title">Acesso Restrito</h2>
    <p className="not-eligible-message">
      O sistema de gamificação está disponível apenas para usuários com planos Premium:
      <br />
      <strong>Growth, Scale, Pro ou Founder</strong>
      <br /><br />
      Faça upgrade do seu plano para desbloquear este recurso e começar a ganhar XP, 
      níveis e conquistas por suas atividades na plataforma!
    </p>
    <div>
      <a 
        href="/organization" 
        className="btn btn-warning btn-lg"
        style={{ padding: '0.75rem 2rem' }}
      >
        <i className="las la-arrow-up me-2"></i>
        Fazer Upgrade
      </a>
    </div>
  </div>
);

const RankingCard = ({ ranking }) => {
  if (!ranking) {
    return (
      <div className="">
        <div className="card-header">
          <h3 className="card-title">
            <i className="las la-trophy"></i>
            Seu Ranking
          </h3>
        </div>
        <div className="loading-container" style={{ padding: '2rem' }}>
          <div className="loading-text">Carregando ranking...</div>
        </div>
      </div>
    );
  }

  const getRankIcon = (rank) => {
    if (rank <= 3) return 'las la-crown';
    if (rank <= 10) return 'las la-medal';
    if (rank <= 50) return 'las la-star';
    return 'las la-user';
  };

  const getRankColor = (rank) => {
    if (rank === 1) return 'var(--warning)';
    if (rank === 2) return 'var(--gray-400)';
    if (rank === 3) return '#CD7F32';
    return 'var(--primary)';
  };

  return (
    <div className="">
      <div className="card-header">
        <h3 className="card-title">
          <i className="las la-trophy"></i>
          Seu Ranking
        </h3>
      </div>
      
      <div style={{ textAlign: 'center', padding: '1rem 0' }}>
        <div 
          style={{ 
            fontSize: '3rem', 
            color: getRankColor(ranking.rank),
            marginBottom: '0.5rem'
          }}
        >
          <i className={getRankIcon(ranking.rank)}></i>
        </div>
        
        <div className="stat-value" style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>
          #{ranking.rank}
        </div>
        
        <div className="stat-label" style={{ marginBottom: '1rem' }}>
          de {ranking.total_users} usuários
        </div>
        
        <div className="tier-badge tier-{ranking.tier?.tier || 'bronze'}" style={{ fontSize: '0.8rem' }}>
          Percentil {ranking.percentile?.toFixed(1)}%
        </div>
      </div>
    </div>
  );
};

export default GamificationDashboard;